import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../config/api';
import {
  CONTRACT_STATUS, LEVEL_STATUS_LABEL, MEMBER_STATUS_LABEL, PARTY_TYPES
} from './constants';
import { formatBaht, splitRemoteErrors, addMonthsIso } from './payload';

const fmtDateTime = (v) =>
  v ? new Date(v).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' }) : '-';

const partyLabel = (value) => PARTY_TYPES.find(p => p.value === value)?.label || value || '-';

const dayAfter = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00Z`);
  if (isNaN(d.getTime())) return '';
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
};

// Statuses from which the API accepts a renewal ("must not be awaiting, draft or pending").
const RENEWABLE = ['approved', 'expired', 'cancelled', 'terminated'];

// Signing state -> colour. Green = signed/approved, amber = waiting on someone,
// red ONLY when it was rejected or cancelled, grey = not started yet.
const approvalTone = (status) => {
  if (status === 'approved') return 'green';
  if (status === 'pending') return 'amber';
  if (['terminated', 'canceled', 'cancelled', 'expired'].includes(status)) return 'red';
  return 'gray';
};

/**
 * Where the contract stands on the e-Contract side, plus the things you can do
 * to it once it exists: refresh status, download the signed PDF, cancel an
 * approved contract, renew one. Everything else on this page only matters
 * until the contract is created.
 */
const ContractStatusPanel = ({
  orderId, contractNumber, snapshot, syncedAt, hasSignedPdf, renewals, mock, onSynced, onLog, onRenewed
}) => {
  const [refreshing, setRefreshing] = useState(false);
  const [mode, setMode] = useState(null); // 'cancel' | 'renew' | null
  const [busy, setBusy] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [formNote, setFormNote] = useState('');

  const [cancelForm, setCancelForm] = useState({
    contract_terminated_at: new Date().toISOString().slice(0, 10),
    terminate_contract_reason: ''
  });
  // A renewal picks up the day AFTER the original term ends (the spec's own
  // example: original ends 31 Aug 2027, renewal starts 1 Sep 2027) and, unless
  // changed, runs for the same number of months.
  const [renewForm, setRenewForm] = useState(() => {
    const start = dayAfter(snapshot?.contract_end_date);
    return {
      contract_start_date: start,
      contract_end_date: addMonthsIso(start, snapshot?.rental_period),
      rental_amount_per_month: ''
    };
  });

  const status = snapshot?.status || null;
  const statusInfo = status ? (CONTRACT_STATUS[status] || { label: status, tone: 'gray' }) : null;
  const base = `${API_URL}/rental/${orderId}/econtract`;

  const openMode = (next) => {
    setMode(mode === next ? null : next);
    setFormErrors({});
    setFormNote('');
  };

  const refresh = async (silent = false) => {
    setRefreshing(true);
    try {
      const resp = await axios.get(`${base}/status`);
      onSynced({ snapshot: resp.data.data, log: resp.data.log, syncedAt: new Date().toISOString() });
      if (!silent) toast.success(`สถานะสัญญา: ${CONTRACT_STATUS[resp.data.data.status]?.label || resp.data.data.status}`);
    } catch (error) {
      const body = error.response?.data;
      if (body?.log) onLog(body.log);
      toast.error(body?.hint || body?.data?.detail || body?.message || 'ซิงก์สถานะไม่สำเร็จ');
    } finally {
      setRefreshing(false);
    }
  };

  // The signed PDF holds customers' details, so it goes through the
  // authenticated route as a blob — never a plain link with a token in the URL.
  const downloadPdf = async () => {
    try {
      const resp = await axios.get(`${base}/signed-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${contractNumber}-signed.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('ดาวน์โหลดไฟล์สัญญาไม่สำเร็จ');
    }
  };

  const submit = async (action, body) => {
    setBusy(true);
    setFormErrors({});
    setFormNote('');
    try {
      const resp = await axios.post(`${base}/${action}`, body);
      onLog(resp.data.log);
      toast.success(action === 'cancel' ? 'ยกเลิกสัญญาแล้ว' : `สร้างสัญญาต่ออายุ ${resp.data.data?.contract_number || ''} แล้ว`);
      if (action === 'renew') {
        onRenewed({
          contract_number: resp.data.data?.contract_number || '',
          contract_start_date: body.contract_start_date,
          contract_end_date: body.contract_end_date,
          created_at: new Date().toISOString(),
          mock: resp.data.mock
        });
      }
      setMode(null);
      await refresh(true);
    } catch (error) {
      const body2 = error.response?.data;
      if (body2?.log) onLog(body2.log);
      const { fieldErrors, general } = splitRemoteErrors(body2?.data);
      setFormErrors(fieldErrors);
      setFormNote([body2?.hint, ...general].filter(Boolean).join(' · ') || body2?.message || 'ดำเนินการไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  const err = (field) => formErrors[field] && <div className="econ-field-error">{formErrors[field]}</div>;

  return (
    <div className="econ-status">
      <div className="econ-status__head">
        <div>
          <div className="econ-status__label">สถานะสัญญาบน e-Contract</div>
          <div className="econ-status__main">
            <span className="econ-status__number">{contractNumber}</span>
            {statusInfo ? (
              <span className={`econ-badge econ-badge--${statusInfo.tone}`}>{statusInfo.label}</span>
            ) : (
              <span className="econ-badge econ-badge--gray">ยังไม่ได้ซิงก์สถานะ</span>
            )}
            {mock && <span className="econ-badge econ-badge--amber">จำลอง/beta</span>}
          </div>
          <div className="econ-status__sub">
            ซิงก์ล่าสุด: {fmtDateTime(syncedAt)}
            {status === 'cancelled' && snapshot?.cancelled_reason ? ` · เหตุผลที่ยกเลิก: ${snapshot.cancelled_reason}` : ''}
          </div>
        </div>
        <div className="econ-status__actions">
          <button type="button" className="rental-form__btn rental-form__btn--secondary" disabled={refreshing} onClick={() => refresh(false)}>
            {refreshing ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="bi bi-arrow-repeat me-2"></i>}
            รีเฟรชสถานะ
          </button>
          {hasSignedPdf && (
            <button type="button" className="rental-form__btn rental-form__btn--primary" onClick={downloadPdf}>
              <i className="bi bi-file-earmark-pdf me-2"></i>ดาวน์โหลดสัญญาที่ลงนามแล้ว
            </button>
          )}
        </div>
      </div>

      {status && status !== 'awaiting' && (
        <div className="econ-status__notice">
          <i className="bi bi-lock-fill me-2"></i>
          สัญญานี้อยู่ในสถานะ "{statusInfo?.label}" — e-Contract ให้แก้ไขข้อมูลได้เฉพาะตอนที่สถานะเป็น "รอรับงาน" จึงปิดการส่งอัปเดตไว้
        </div>
      )}

      {snapshot && (
        <div className="econ-status__grid">
          <div>
            <div className="econ-mini-title">ยอดที่ e-Contract คำนวณ</div>
            <table className="econ-table">
              <tbody>
                <tr><td>ค่าเช่า / เดือน</td><td>฿{formatBaht(snapshot.rental_amount_per_month)}</td></tr>
                <tr><td>VAT ค่าเช่า</td><td>฿{formatBaht(snapshot.rental_vat)}</td></tr>
                <tr className="econ-table__total"><td>รวมค่าเช่า</td><td>฿{formatBaht(snapshot.total_rental_amount)}</td></tr>
                <tr><td>ค่าขนส่งและติดตั้ง รวม VAT</td><td>฿{formatBaht(snapshot.total_transport_amount)}</td></tr>
                <tr><td>เงินประกัน รวม VAT</td><td>฿{formatBaht(snapshot.total_deposit_amount)}</td></tr>
                <tr><td>วันสิ้นสุดการเช่า (คำนวณ)</td><td>{snapshot.rental_end_date || '-'}</td></tr>
              </tbody>
            </table>
          </div>

          <div>
            <div className="econ-mini-title">ความคืบหน้าการลงนาม</div>
            {Array.isArray(snapshot.loa_detail) && snapshot.loa_detail.length > 0 ? (
              <div className="econ-approval">
                {snapshot.loa_detail.map(level => (
                  <div className="econ-approval__level" key={level.level}>
                    <div className="econ-approval__head">
                      <strong>ลำดับ {level.level}</strong> · {partyLabel(level.contract_party_type)}
                      <span className={`econ-approval__progress econ-state--${approvalTone(level.status)}`}>
                        {approvalTone(level.status) === 'green' && <i className="bi bi-check-circle-fill"></i>}
                        {level.progress?.current ?? 0}/{level.progress?.total ?? '-'} · {LEVEL_STATUS_LABEL[level.status] || level.status}
                      </span>
                    </div>
                    {(level.members || []).map((m, i) => (
                      <div className="econ-approval__member" key={i}>
                        <span>{m.full_name || m.email || '-'}</span>
                        <span className={`econ-approval__member-status econ-state--${approvalTone(m.status)}`}>
                          {approvalTone(m.status) === 'green' && <i className="bi bi-check-circle-fill"></i>}
                          {MEMBER_STATUS_LABEL[m.status] || m.status}
                          {m.date_of_approve ? ` · ${fmtDateTime(m.date_of_approve)}` : ''}
                          {m.reason ? ` · ${m.reason}` : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <div className="econ-status__sub">ไม่มีข้อมูลลำดับการลงนาม</div>
            )}
          </div>
        </div>
      )}

      <div className="econ-status__tools">
        {status === 'approved' && (
          <button type="button" className="rental-form__btn rental-form__btn--secondary" onClick={() => openMode('cancel')}>
            <i className="bi bi-x-octagon me-2"></i>ยกเลิกสัญญา
          </button>
        )}
        {RENEWABLE.includes(status) && (
          <button type="button" className="rental-form__btn rental-form__btn--secondary" onClick={() => openMode('renew')}>
            <i className="bi bi-arrow-repeat me-2"></i>ต่ออายุสัญญา
          </button>
        )}
        {!status && <span className="econ-status__sub">กด "รีเฟรชสถานะ" เพื่อดูว่าสัญญาอยู่ในขั้นตอนใด และเปิดการยกเลิก/ต่ออายุ</span>}
      </div>

      {mode === 'cancel' && (
        <div className="econ-inline-form">
          <div className="econ-mini-title">ยกเลิกสัญญา {contractNumber}</div>
          <div className="rental-form__grid">
            <div className="rental-form__field">
              <label>วันที่ยกเลิกสัญญา <span className="required">*</span></label>
              <input type="date" className="rental-form__input" value={cancelForm.contract_terminated_at}
                onChange={(e) => setCancelForm(p => ({ ...p, contract_terminated_at: e.target.value }))} />
              {err('contract_terminated_at')}
            </div>
            <div className="rental-form__field">
              <label>เหตุผลการยกเลิก <span className="required">*</span></label>
              <input type="text" maxLength="255" className="rental-form__input" value={cancelForm.terminate_contract_reason}
                placeholder="เช่น ลูกค้ายกเลิกสัญญาก่อนกำหนด"
                onChange={(e) => setCancelForm(p => ({ ...p, terminate_contract_reason: e.target.value }))} />
              {err('terminate_contract_reason')}
            </div>
          </div>
          {formNote && <div className="econ-field-error">{formNote}</div>}
          <div className="econ-inline-form__actions">
            <button type="button" className="rental-form__btn rental-form__btn--secondary" onClick={() => setMode(null)}>ปิด</button>
            <button type="button" className="rental-form__btn econ-btn-danger" disabled={busy}
              onClick={() => {
                if (window.confirm(`ยืนยันยกเลิกสัญญา ${contractNumber}? การยกเลิกไม่สามารถย้อนกลับได้`)) submit('cancel', cancelForm);
              }}>
              {busy ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="bi bi-x-octagon me-2"></i>}
              ยืนยันยกเลิกสัญญา
            </button>
          </div>
        </div>
      )}

      {mode === 'renew' && (
        <div className="econ-inline-form">
          <div className="econ-mini-title">ต่ออายุสัญญา {contractNumber} (สร้างสัญญาฉบับใหม่ที่อ้างอิงฉบับเดิม)</div>
          <div className="rental-form__grid">
            <div className="rental-form__field">
              <label>ค่าเช่าต่อเดือนใหม่ (ถ้าเปลี่ยน)</label>
              <div className="econ-money-field">
                <span className="econ-money-field__prefix">฿</span>
                <input type="number" min="0" step="0.01" value={renewForm.rental_amount_per_month}
                  onChange={(e) => setRenewForm(p => ({ ...p, rental_amount_per_month: e.target.value }))} />
              </div>
              {err('rental_amount_per_month')}
            </div>
            <div className="rental-form__field">
              <label>วันที่เริ่มสัญญาใหม่ <span className="required">*</span></label>
              <input type="date" className="rental-form__input" value={renewForm.contract_start_date}
                onChange={(e) => setRenewForm(p => ({
                  ...p,
                  contract_start_date: e.target.value,
                  // Same term as the original unless staff say otherwise.
                  contract_end_date: p.contract_end_date || addMonthsIso(e.target.value, snapshot?.rental_period)
                }))} />
              {err('contract_start_date')}
            </div>
            <div className="rental-form__field">
              <label>วันที่สิ้นสุดสัญญาใหม่ <span className="required">*</span></label>
              <input type="date" className="rental-form__input" value={renewForm.contract_end_date}
                onChange={(e) => setRenewForm(p => ({ ...p, contract_end_date: e.target.value }))} />
              {err('contract_end_date')}
            </div>
          </div>
          <div className="rental-form__hint">ข้อมูลอื่นทั้งหมด (ผู้ลงนาม รายการเครื่อง ข้อตกลง ฯลฯ) จะคัดลอกจากสัญญาเดิมโดย e-Contract</div>
          {formNote && <div className="econ-field-error">{formNote}</div>}
          <div className="econ-inline-form__actions">
            <button type="button" className="rental-form__btn rental-form__btn--secondary" onClick={() => setMode(null)}>ปิด</button>
            <button type="button" className="rental-form__btn rental-form__btn--primary" disabled={busy} onClick={() => submit('renew', renewForm)}>
              {busy ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="bi bi-arrow-repeat me-2"></i>}
              สร้างสัญญาต่ออายุ
            </button>
          </div>
        </div>
      )}

      {renewals.length > 0 && (
        <div className="econ-renewals">
          <div className="econ-mini-title">สัญญาที่ต่ออายุจากฉบับนี้</div>
          {renewals.map((r, i) => (
            <div className="econ-renewals__item" key={i}>
              <strong>{r.contract_number}</strong>
              <span>{r.contract_start_date} → {r.contract_end_date}{r.mock ? ' (จำลอง)' : ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContractStatusPanel;
